import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { useCheckTypes, useWorkflowStatus, useClarificationTypes } from '../hooks/useMasterData';
import SearchableDropdown from './SearchableDropdown';
import USDateInput from './USDateInput';
import Tooltip from './Tooltip';
import { Info, BookmarkPlus, ChevronDown } from 'lucide-react';
import { filterEmojis } from '../utils/emojiFilter';
import './AdvancedFilterDrawer.css';

/** Drawer-only: remembers Exclude mode when no values selected yet (stripped on Apply). */
const FILTER_EXCLUDE_UI_INTENT_KEY = '_filterExcludeUiIntent';

const EXCLUDE_FIELD_TO_INCLUDE_FIELD = {
  excludeStatuses: 'statuses',
  excludeWorkflowStatuses: 'workflowStatuses',
  excludePracticeCodes: 'practiceCodes',
  excludeLocationCodes: 'locationCodes',
  excludeCheckTypes: 'checkTypes',
  excludeClarificationTypes: 'clarificationType',
  excludeAssigneeIds: 'assigneeIds',
  excludeReporterIds: 'reporterIds',
  excludeReporteeIds: 'reporteeIds'
};

const AdvancedFilterDrawer = ({ isOpen, onClose, filters, onApplyFilters, onResetFilters, filterType = null, isClarifications = false, isDitDrl = false, isUnknownPage = false, isTrashPage = false }) => {
  const EXCLUDE_FIELD_MAP = {
    statuses: 'excludeStatuses',
    workflowStatuses: 'excludeWorkflowStatuses',
    practiceCodes: 'excludePracticeCodes',
    locationCodes: 'excludeLocationCodes',
    checkTypes: 'excludeCheckTypes',
    clarificationType: 'excludeClarificationTypes',
    assigneeIds: 'excludeAssigneeIds',
    reporterIds: 'excludeReporterIds',
    reporteeIds: 'excludeReporteeIds'
  };
  const DIT_DRL_EXCLUDE_FIELDS = new Set(['assigneeIds', 'reporteeIds', 'reporterIds']);

  const { showError, showSuccess } = useNotification();
  const [localFilters, setLocalFilters] = useState(filters);
  const { users } = useUsers(); // Get users from context
  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const { data: checkTypes, loading: checkTypesLoading } = useCheckTypes(true);
  const { data: workflowStatuses, loading: workflowStatusesLoading } = useWorkflowStatus(true);
  const { data: clarificationTypes, loading: clarificationTypesLoading } = useClarificationTypes(true);

  // Saved filters state
  const [savedFilters, setSavedFilters] = useState([]);
  const [isLoadingSavedFilters, setIsLoadingSavedFilters] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [saveFilterOrder, setSaveFilterOrder] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState('');
  
  // Refs for "To" date inputs to auto-open calendar
  const depositDateToRef = useRef(null);
  const receivedDateToRef = useRef(null);
  const completedDateToRef = useRef(null);
  const resolvedDateToRef = useRef(null);
  const openedDateToRef = useRef(null);
  const dateReceivedToRef = useRef(null);
  const endDateRef = useRef(null);
  const reconciliationDateToRef = useRef(null);
  const completedDateToRefDrl = useRef(null);
  const createdAtToRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({
        ...filters,
        [FILTER_EXCLUDE_UI_INTENT_KEY]: {}
      });
      setSelectedSavedFilterId('');
      fetchPractices();
      if (filterType) {
        fetchSavedFilters();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filters, isClarifications, filterType]);

  useEffect(() => {
    const selectedPracticeCodes = [
      ...(localFilters.practiceCodes || []),
      ...(localFilters.excludePracticeCodes || [])
    ];

    if (selectedPracticeCodes.length > 0 && practices.length > 0) {
      // Fetch locations for all selected practices
      const fetchAllLocations = async () => {
        const allLocations = [];
        for (const practiceCode of [...new Set(selectedPracticeCodes)]) {
          try {
            const practice = practices.find(p => p.code === practiceCode);
            if (practice && practice.practiceId) {
              const response = await api.getPracticeLocations(practice.practiceId);
              const locationsList = Array.isArray(response) ? response : (response?.items || []);
              allLocations.push(...locationsList);
            }
          } catch (error) {
            console.error(`Error fetching locations for practice ${practiceCode}:`, error);
            const errorMessage = error?.message || error?.data?.message || `Failed to fetch locations for practice ${practiceCode}`;
            showError(errorMessage);
          }
        }
        setLocations(allLocations);
      };
      fetchAllLocations();
    } else {
      setLocations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.practiceCodes, localFilters.excludePracticeCodes, practices, isDitDrl]);

  // Removed: Users are now loaded from context on login, no need to fetch here

  const fetchPractices = async () => {
    try {
      const response = await api.getAllPractices();
      if (response && Array.isArray(response)) {
        setPractices(response);
      } else if (response && response.items) {
        setPractices(response.items);
      }
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
    }
  };

  // Helper function to calculate max date (3 months from start date)
  const calculateMaxDate = (startDate) => {
    if (!startDate || startDate.trim() === '') return undefined;
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + 3);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleChange = (field, value) => {
    // Filter emojis from string inputs
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    
    // For array fields, don't update directly - use handleAddToArray instead
    const arrayFields = [
      'statuses',
      'practiceCodes',
      'locationCodes',
      'assigneeIds',
      'reporterIds',
      'reporteeIds',
      'reconciliationStatuses',
      'checkTypes',
      'workflowStatuses',
      'assigneeTypes',
      'reporteeTypes',
      'excludeStatuses',
      'excludeWorkflowStatuses',
      'excludePracticeCodes',
      'excludeLocationCodes',
      'excludeCheckTypes',
      'excludeClarificationTypes',
      'excludeAssigneeIds',
      'excludeReporterIds',
      'excludeReporteeIds'
    ];
    if (arrayFields.includes(field)) {
      // This is handled by handleAddToArray
      return;
    }
    
    setLocalFilters(prev => {
      const next = { ...prev, [field]: filteredValue };
      if (field === 'clarificationType') {
        const intent = { ...(prev[FILTER_EXCLUDE_UI_INTENT_KEY] || {}) };
        delete intent.clarificationType;
        next[FILTER_EXCLUDE_UI_INTENT_KEY] = intent;
      }
      return next;
    });

    // Clear location if practice changes
    if (field === 'practiceCode' && filteredValue !== localFilters.practiceCode) {
      setLocalFilters(prev => ({
        ...prev,
        locationCode: ''
      }));
    }
  };

  const handleAddToArray = (field, value) => {
    if (!value || value === '') return;
    
    setLocalFilters(prev => {
      const currentArray = prev[field] || [];
      // Don't add if already exists
      if (currentArray.includes(value)) {
        return prev;
      }
      const includeField = EXCLUDE_FIELD_TO_INCLUDE_FIELD[field];
      const intent = { ...(prev[FILTER_EXCLUDE_UI_INTENT_KEY] || {}) };
      if (includeField) {
        delete intent[includeField];
      }
      return {
        ...prev,
        [field]: [...currentArray, value],
        [FILTER_EXCLUDE_UI_INTENT_KEY]: intent
      };
    });
  };

  const handleRemoveFromArray = (field, value) => {
    setLocalFilters(prev => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.filter(item => item !== value);
      const next = {
        ...prev,
        [field]: newArray
      };
      const includeField = EXCLUDE_FIELD_TO_INCLUDE_FIELD[field];
      if (includeField && newArray.length === 0) {
        next[FILTER_EXCLUDE_UI_INTENT_KEY] = {
          ...(prev[FILTER_EXCLUDE_UI_INTENT_KEY] || {}),
          [includeField]: true
        };
      }
      if (field === 'practiceCodes' || field === 'excludePracticeCodes') {
        next.locationCodes = [];
        next.excludeLocationCodes = [];
      }
      return next;
    });
  };

  const isExcludeMode = (field) => {
    if (isDitDrl && !DIT_DRL_EXCLUDE_FIELDS.has(field)) return false;
    const excludeField = EXCLUDE_FIELD_MAP[field];
    if (!excludeField) return false;
    if (field === 'clarificationType') {
      const arr = localFilters.excludeClarificationTypes || [];
      if (arr.length > 0) return true;
      return !!localFilters[FILTER_EXCLUDE_UI_INTENT_KEY]?.[field];
    }
    const excludeValues = localFilters[excludeField];
    if (Array.isArray(excludeValues) && excludeValues.length > 0) return true;
    return !!localFilters[FILTER_EXCLUDE_UI_INTENT_KEY]?.[field];
  };

  const getActiveField = (field) => (isExcludeMode(field) ? EXCLUDE_FIELD_MAP[field] : field);

  const getSelectedValues = (field) => {
    if (field === 'clarificationType') {
      if (isExcludeMode(field)) {
        return localFilters.excludeClarificationTypes || [];
      }
      return localFilters.clarificationType ? [localFilters.clarificationType] : [];
    }
    return localFilters[getActiveField(field)] || [];
  };

  const handleModeToggle = (field, mode) => {
    if ((isDitDrl && !DIT_DRL_EXCLUDE_FIELDS.has(field)) || !EXCLUDE_FIELD_MAP[field]) return;
    const excludeField = EXCLUDE_FIELD_MAP[field];

    setLocalFilters(prev => {
      const intent = { ...(prev[FILTER_EXCLUDE_UI_INTENT_KEY] || {}) };

      if (field === 'clarificationType') {
        const includeValue = prev.clarificationType;
        const excludeValues = prev[excludeField] || [];

        if (mode === 'exclude') {
          const newEx = includeValue ? [includeValue] : excludeValues;
          if (newEx.length === 0) intent[field] = true;
          else delete intent[field];
          return {
            ...prev,
            clarificationType: '',
            [excludeField]: newEx,
            [FILTER_EXCLUDE_UI_INTENT_KEY]: intent
          };
        }
        delete intent[field];
        return {
          ...prev,
          clarificationType: excludeValues[0] || '',
          [excludeField]: [],
          [FILTER_EXCLUDE_UI_INTENT_KEY]: intent
        };
      }

      const includeValues = prev[field] || [];
      const excludeValues = prev[excludeField] || [];
      if (mode === 'exclude') {
        const newExclude = includeValues.length > 0 ? includeValues : excludeValues;
        if (newExclude.length === 0) intent[field] = true;
        else delete intent[field];
        return {
          ...prev,
          [field]: [],
          [excludeField]: newExclude,
          [FILTER_EXCLUDE_UI_INTENT_KEY]: intent
        };
      }
      delete intent[field];
      return {
        ...prev,
        [field]: excludeValues.length > 0 ? excludeValues : includeValues,
        [excludeField]: [],
        [FILTER_EXCLUDE_UI_INTENT_KEY]: intent
      };
    });
  };

  const handleAddFilterValue = (field, value) => {
    if (!value) return;
    if (field === 'clarificationType') {
      if (isExcludeMode(field)) {
        handleAddToArray('excludeClarificationTypes', value);
      } else {
        handleChange('clarificationType', value);
      }
      return;
    }
    handleAddToArray(getActiveField(field), value);
  };

  const handleRemoveFilterValue = (field, value) => {
    if (field === 'clarificationType') {
      if (isExcludeMode(field)) {
        handleRemoveFromArray('excludeClarificationTypes', value);
      } else {
        handleChange('clarificationType', '');
      }
      return;
    }
    handleRemoveFromArray(getActiveField(field), value);
  };

  const renderFilterLabel = (field, label) => {
    if ((isDitDrl && !DIT_DRL_EXCLUDE_FIELDS.has(field)) || !EXCLUDE_FIELD_MAP[field]) {
      return <label>{label}</label>;
    }

    const excludeActive = isExcludeMode(field);
    return (
      <div className="filter-label-row">
        <label className={excludeActive ? 'exclude-mode-label' : ''}>{label}</label>
        <div className="include-exclude-controls">
          <button
            type="button"
            className={`filter-mode-switch ${excludeActive ? 'filter-mode-switch--exclude' : 'filter-mode-switch--include'}`}
            role="switch"
            aria-checked={excludeActive}
            aria-label={excludeActive ? 'Exclude mode: selected values are excluded' : 'Include mode: selected values are included'}
            onClick={() => handleModeToggle(field, excludeActive ? 'include' : 'exclude')}
          >
            <span className="filter-mode-switch-thumb" aria-hidden />
          </button>
          <span className={`filter-mode-switch-caption ${excludeActive ? 'filter-mode-switch-caption--exclude' : ''}`}>
            {excludeActive ? 'Exclude' : 'Include'}
          </span>
          <Tooltip text="Include returns matching records. Exclude removes selected values for this column." position="top">
            <span className="exclude-help-dot" aria-label="About include and exclude mode">!</span>
          </Tooltip>
        </div>
      </div>
    );
  };

  const handleDateChange = (field, value) => {
    // Check if this is an end date field being set without a start date
    const endDateFields = {
      'depositDateTo': 'depositDateFrom',
      'receivedDateTo': 'receivedDateFrom',
      'completedDateTo': 'completedDateFrom',
      'resolvedDateTo': 'resolvedDateFrom',
      'openedDateTo': 'openedDateFrom',
      'drlReceivedTo': 'drlReceivedFrom',
      'drlReceivedDateTo': 'drlReceivedDateFrom',
      'createdAtTo': 'createdAtFrom',
      'reconciliationDateTo': 'reconciliationDateFrom',
      'endDate': 'startDate'
    };
    
    // Reverse mapping: start date fields to end date fields (for clearing end date when start date is cleared)
    const startDateFields = {
      'depositDateFrom': 'depositDateTo',
      'receivedDateFrom': 'receivedDateTo',
      'completedDateFrom': 'completedDateTo',
      'resolvedDateFrom': 'resolvedDateTo',
      'openedDateFrom': 'openedDateTo',
      'drlReceivedFrom': 'drlReceivedTo',
      'drlReceivedDateFrom': 'drlReceivedDateTo',
      'createdAtFrom': 'createdAtTo',
      'reconciliationDateFrom': 'reconciliationDateTo',
      'startDate': 'endDate'
    };
    
    // If user is clearing a start date, also clear the corresponding end date
    if (startDateFields[field] && (!value || value.trim() === '')) {
      const endDateField = startDateFields[field];
      setLocalFilters(prev => ({
        ...prev,
        [field]: '',
        [endDateField]: '' // Clear the corresponding end date
      }));
      return; // Exit early after clearing both dates
    }
    
    // If user is trying to set an end date without a start date, show error and prevent
    if (endDateFields[field] && value && value.trim() !== '') {
      const startDateField = endDateFields[field];
      const startDateValue = localFilters[startDateField];
      
      if (!startDateValue || startDateValue.trim() === '') {
        const fieldLabels = {
          'depositDateTo': 'Deposit Date',
          'receivedDateTo': 'Received Date',
          'completedDateTo': isDitDrl ? 'Posting Completed' : 'Completed Date',
          'resolvedDateTo': 'Resolved Date',
          'openedDateTo': 'Opened Date',
          'drlReceivedTo': 'DRL Received',
          'drlReceivedDateTo': 'DRL Received Date',
          'createdAtTo': 'DRL Created',
          'reconciliationDateTo': 'Reconciliation',
          'endDate': 'Date Range'
        };
        showError(`First you need to select start date for ${fieldLabels[field] || 'the date range'}.`);
        return; // Don't set the value
      }
      
      // Validate 3-month max range
      const startDate = new Date(startDateValue);
      const endDate = new Date(value);
      
      // Calculate difference in months
      const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                         (endDate.getMonth() - startDate.getMonth());
      
      if (monthsDiff > 3) {
        const fieldLabels = {
          'depositDateTo': 'Deposit Date',
          'receivedDateTo': 'Received Date',
          'completedDateTo': isDitDrl ? 'Posting Completed' : 'Completed Date',
          'resolvedDateTo': 'Resolved Date',
          'openedDateTo': 'Opened Date',
          'drlReceivedTo': 'DRL Received',
          'drlReceivedDateTo': 'DRL Received Date',
          'createdAtTo': 'DRL Created',
          'reconciliationDateTo': 'Reconciliation',
          'endDate': 'Date Range'
        };
        showError(`${fieldLabels[field] || 'Date range'} can only be maximum 3 months.`);
        return; // Don't set the value
      }
    }
    
    setLocalFilters(prev => ({
      ...prev,
      [field]: value || ''
    }));
    
    // Auto-open corresponding "To" date calendar when "From" date is selected
    if (value && value.trim() !== '') {
      const datePairMap = {
        'depositDateFrom': depositDateToRef,
        'receivedDateFrom': receivedDateToRef,
        'completedDateFrom': isDitDrl ? completedDateToRefDrl : completedDateToRef,
        'resolvedDateFrom': resolvedDateToRef,
        'openedDateFrom': openedDateToRef,
        'dateReceivedFrom': dateReceivedToRef,
        'startDate': endDateRef,
        'drlReceivedFrom': dateReceivedToRef,
        'drlReceivedDateFrom': dateReceivedToRef,
        'createdAtFrom': createdAtToRef,
        'reconciliationDateFrom': reconciliationDateToRef
      };
      
      const toDateRef = datePairMap[field];
      if (toDateRef && toDateRef.current) {
        // Use a small delay to ensure the date value is processed first
        setTimeout(() => {
          toDateRef.current?.openCalendar();
        }, 300);
      }
    }
  };

  // ─── Saved Filters helpers ────────────────────────────────────────────────
  const fetchSavedFilters = async () => {
    if (!filterType) return;
    setIsLoadingSavedFilters(true);
    try {
      const list = await api.getSavedFilters(filterType);
      setSavedFilters(Array.isArray(list) ? list : []);
    } catch {
      setSavedFilters([]);
    } finally {
      setIsLoadingSavedFilters(false);
    }
  };

  const handleLoadSavedFilter = (id) => {
    setSelectedSavedFilterId(id);
    if (!id) return;
    const found = savedFilters.find(f => String(f.id) === String(id));
    if (found && found.payload) {
      setLocalFilters(prev => ({
        ...prev,
        ...found.payload,
        [FILTER_EXCLUDE_UI_INTENT_KEY]: prev[FILTER_EXCLUDE_UI_INTENT_KEY] || {}
      }));
    }
  };

  const handleSaveFilter = async () => {
    if (!saveFilterName.trim()) {
      showError('Please enter a name for the filter.');
      return;
    }
    setIsSaving(true);
    try {
      // Strip UI-only keys and any sort fields the backend rejects
      const {
        [FILTER_EXCLUDE_UI_INTENT_KEY]: _uiIntent,
        sortBy: _sortBy,
        sortDirection: _sortDir,
        ...payloadToSave
      } = localFilters;
      const displayOrder = saveFilterOrder !== '' ? parseInt(saveFilterOrder, 10) : 0;
      await api.createSavedFilter({
        type: filterType,
        name: saveFilterName.trim(),
        payload: payloadToSave,
        displayOrder: isNaN(displayOrder) ? 0 : displayOrder
      });
      showSuccess(`Filter "${saveFilterName.trim()}" saved successfully.`);
      setSaveFilterName('');
      setSaveFilterOrder('');
      setShowSaveModal(false);
      fetchSavedFilters();
    } catch (e) {
      showError(e?.message || e?.data?.message || 'Failed to save filter. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleApply = () => {
    // Validate date ranges: if a "From" date is selected, the corresponding "To" date must also be selected
    const dateRangePairs = [
      { from: 'depositDateFrom', to: 'depositDateTo', label: 'Deposit Date' },
      { from: 'receivedDateFrom', to: 'receivedDateTo', label: 'Received Date' },
      { from: 'completedDateFrom', to: 'completedDateTo', label: isDitDrl ? 'Posting Completed' : 'Completed Date' },
      { from: 'resolvedDateFrom', to: 'resolvedDateTo', label: 'Resolved Date' },
      { from: 'openedDateFrom', to: 'openedDateTo', label: 'Opened Date' },
      { from: 'dateReceivedFrom', to: 'dateReceivedTo', label: 'Date Received' },
      { from: 'startDate', to: 'endDate', label: 'Date Range' },
      { from: 'drlReceivedFrom', to: 'drlReceivedTo', label: 'DRL Received' },
      { from: 'drlReceivedDateFrom', to: 'drlReceivedDateTo', label: 'DRL Received Date' },
      { from: 'createdAtFrom', to: 'createdAtTo', label: 'DRL Created' },
      { from: 'reconciliationDateFrom', to: 'reconciliationDateTo', label: 'Reconciliation' }
    ];

    // Check each date range pair
    for (const pair of dateRangePairs) {
      const fromValue = localFilters[pair.from];
      const toValue = localFilters[pair.to];
      
      // If "From" date is set but "To" date is not set, show error
      if (fromValue && fromValue.trim() !== '' && (!toValue || toValue.trim() === '')) {
        showError(`Please select an end date for ${pair.label} range. End date is required when start date is selected.`);
        return; // Don't apply filters if validation fails
      }
      
      // If both dates are set, validate 3-month maximum range
      if (fromValue && fromValue.trim() !== '' && toValue && toValue.trim() !== '') {
        const startDate = new Date(fromValue);
        const endDate = new Date(toValue);
        
        // Calculate difference in months
        const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                           (endDate.getMonth() - startDate.getMonth());
        
        if (monthsDiff > 3) {
          showError(`${pair.label} range can only be maximum 3 months.`);
          return; // Don't apply filters if validation fails
        }
      }
    }

    const { [FILTER_EXCLUDE_UI_INTENT_KEY]: _uiIntent, ...filtersForApply } = localFilters;
    onApplyFilters(filtersForApply);
    onClose();
  };

  const handleReset = () => {
    const resetFilters = isClarifications ? (isDitDrl ? {
      // DitDrl Clarifications reset
      statuses: [],
      assigneeIds: [],
      reporterIds: [],
      excludeAssigneeIds: [],
      excludeReporterIds: [],
      excludeReporteeIds: [],
      assigneeTypes: [],
      reporteeTypes: [],
      drlNumber: '',
      drlReceivedDateFrom: '',
      drlReceivedDateTo: '',
      resolvedDateFrom: '',
      resolvedDateTo: '',
      openedDateFrom: '',
      openedDateTo: '',
      startDate: '',
      endDate: '',
      month: null,
      year: null
    } : {
      // Regular Clarifications reset
      statuses: [],
      practiceCodes: [],
      excludeStatuses: [],
      excludePracticeCodes: [],
      excludeLocationCodes: [],
      excludeCheckTypes: [],
      excludeWorkflowStatuses: [],
      excludeClarificationTypes: [],
      locationCodes: [],
      assigneeIds: [],
      reporterIds: [],
      excludeAssigneeIds: [],
      excludeReporterIds: [],
      excludeReporteeIds: [],
      assigneeTypes: [],
      reporteeTypes: [],
      checkTypes: [],
      workflowStatuses: [],
      checkNumber: '',
      clarificationType: '',
      unknown: undefined,
      depositDateFrom: '',
      depositDateTo: '',
      resolvedDateFrom: '',
      resolvedDateTo: '',
      openedDateFrom: '',
      openedDateTo: '',
      startDate: '',
      endDate: '',
      month: null,
      year: null
    }) : isDitDrl ? {
      statuses: [],
      practiceCodes: [],
      locationCodes: [],
      assigneeIds: [],
      reporteeIds: [],
      excludeAssigneeIds: [],
      excludeReporterIds: [],
      excludeReporteeIds: [],
      assigneeTypes: [],
      reporteeTypes: [],
      reconciliationStatuses: [],
      drlNumber: '',
      batchNumber: '',
      drlReceivedFrom: '',
      drlReceivedTo: '',
      reconciliationDateFrom: '',
      reconciliationDateTo: '',
      completedDateFrom: '',
      completedDateTo: '',
      createdAtFrom: '',
      createdAtTo: '',
      month: null,
      year: null
    } : {
      statuses: [],
      practiceCodes: [],
      excludeStatuses: [],
      excludePracticeCodes: [],
      excludeLocationCodes: [],
      excludeCheckTypes: [],
      excludeWorkflowStatuses: [],
      locationCodes: [],
      assigneeIds: [],
      reporterIds: [],
      excludeAssigneeIds: [],
      excludeReporterIds: [],
      excludeReporteeIds: [],
      checkTypes: [],
      workflowStatuses: [],
      checkNumber: '',
      batchNumber: '',
      payer: '',
      unknown: undefined,
      depositDateFrom: '',
      depositDateTo: '',
      receivedDateFrom: '',
      receivedDateTo: '',
      completedDateFrom: '',
      completedDateTo: '',
      startDate: '',
      endDate: '',
      month: null,
      year: null,
      sortBy: '',
      sortDirection: ''
    };
    setLocalFilters({
      ...resetFilters,
      [FILTER_EXCLUDE_UI_INTENT_KEY]: {}
    });
    onResetFilters();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="drawer-overlay" 
        onClick={(e) => {
          // Don't close if clicking on a dropdown popup
          if (!e.target.closest('.searchable-dropdown-popup')) {
            onClose();
          }
        }}
      ></div>
      <div className="advanced-filter-drawer">
        <div className="drawer-header">
          <h2>Advanced Filters</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="drawer-content">
          {/* ── Saved Filters Section ── */}
          {filterType && (
            <div className="saved-filters-section">
              <div className="saved-filters-header">
                <BookmarkPlus size={15} />
                <span>Saved Filters</span>
              </div>
              <div className="saved-filters-select-row">
                <div className="saved-filters-select-wrapper">
                  <select
                    className="saved-filters-select"
                    value={selectedSavedFilterId}
                    onChange={e => handleLoadSavedFilter(e.target.value)}
                    disabled={isLoadingSavedFilters}
                  >
                    <option value="">
                      {isLoadingSavedFilters ? 'Loading...' : savedFilters.length === 0 ? 'No saved filters yet' : '— Select a saved filter —'}
                    </option>
                    {savedFilters.map(sf => (
                      <option key={sf.id} value={sf.id}>{sf.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="saved-filters-chevron" />
                </div>
              </div>
              {selectedSavedFilterId && (
                <p className="saved-filters-hint">Filter loaded — adjust values above then click Apply.</p>
              )}
            </div>
          )}

          <div className="filter-group">
            {renderFilterLabel('statuses', 'Status')}
            <div className="multi-select-wrapper">
              <div className="multi-select-input">
                <SearchableDropdown
                  options={[
                    { value: '', label: 'Select Status' },
                    ...(isClarifications ? [
                      { value: 'OPEN', label: 'Open' },
                      { value: 'RESOLVED', label: 'Resolved' }
                    ] : [
                      { value: 'NOT_STARTED', label: 'Not Started' },
                      { value: 'IN_PROGRESS', label: 'In Progress' },
                      { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
                      { value: 'COMPLETED', label: 'Completed' },
                      { value: 'OVER_POSTED', label: 'Over Posted' }
                    ])
                  ].filter(opt => opt.value === '' || !getSelectedValues('statuses').includes(opt.value))}
                  value=""
                  onChange={(value) => {
                    if (value) handleAddFilterValue('statuses', value);
                  }}
                  placeholder="Select Status"
                  maxVisibleItems={5}
                />
              </div>
              {getSelectedValues('statuses').length > 0 && (
                <div className="selected-chips">
                  {getSelectedValues('statuses').map((status) => {
                    const statusOptions = isClarifications ? [
                      { value: 'OPEN', label: 'Open' },
                      { value: 'RESOLVED', label: 'Resolved' }
                    ] : [
                      { value: 'NOT_STARTED', label: 'Not Started' },
                      { value: 'IN_PROGRESS', label: 'In Progress' },
                      { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
                      { value: 'COMPLETED', label: 'Completed' },
                      { value: 'OVER_POSTED', label: 'Over Posted' }
                    ];
                    const label = statusOptions.find(opt => opt.value === status)?.label || status;
                    return (
                      <div key={status} className="selected-chip">
                        <span>{isExcludeMode('statuses') ? `Not ${label}` : label}</span>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          onClick={() => handleRemoveFilterValue('statuses', status)}
                          title="Remove"
                        >
                          <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            {renderFilterLabel('practiceCodes', 'Practice')}
            <div className="multi-select-wrapper">
              <div className="multi-select-input">
                <SearchableDropdown
                  options={[
                    { value: '', label: 'Select Practice' },
                    ...practices
                      .filter(p => p.isActive !== false)
                      .filter(p => !getSelectedValues('practiceCodes').includes(p.code))
                      .map(practice => ({
                        value: practice.code,
                        label: practice.name || practice.code
                      }))
                  ]}
                  value=""
                  onChange={(value) => {
                    if (value) handleAddFilterValue('practiceCodes', value);
                  }}
                  placeholder="Select Practice"
                  maxVisibleItems={5}
                />
              </div>
              {getSelectedValues('practiceCodes').length > 0 && (
                <div className="selected-chips">
                  {getSelectedValues('practiceCodes').map((practiceCode) => {
                    const practice = practices.find(p => p.code === practiceCode);
                    const label = practice?.name || practiceCode;
                    return (
                      <div key={practiceCode} className="selected-chip">
                        <span>{isExcludeMode('practiceCodes') ? `Not ${label}` : label}</span>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          onClick={() => handleRemoveFilterValue('practiceCodes', practiceCode)}
                          title="Remove"
                        >
                          <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            {renderFilterLabel('locationCodes', 'Location')}
            <div className="multi-select-wrapper">
              <div className="multi-select-input">
                <SearchableDropdown
                  options={[
                    { value: '', label: 'Select Location' },
                    ...locations
                      .filter(l => l.isActive !== false)
                      .filter(l => !getSelectedValues('locationCodes').includes(l.code))
                      .map(location => ({
                        value: location.code,
                        label: location.name || location.code
                      }))
                  ]}
                  value=""
                  onChange={(value) => {
                    if (value) handleAddFilterValue('locationCodes', value);
                  }}
                  placeholder="Select Location"
                  disabled={!((localFilters.practiceCodes && localFilters.practiceCodes.length > 0) || (localFilters.excludePracticeCodes && localFilters.excludePracticeCodes.length > 0))}
                  maxVisibleItems={5}
                />
              </div>
              {getSelectedValues('locationCodes').length > 0 && (
                <div className="selected-chips">
                  {getSelectedValues('locationCodes').map((locationCode) => {
                    const location = locations.find(l => l.code === locationCode);
                    const label = location?.name || locationCode;
                    return (
                      <div key={locationCode} className="selected-chip">
                        <span>{isExcludeMode('locationCodes') ? `Not ${label}` : label}</span>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          onClick={() => handleRemoveFilterValue('locationCodes', locationCode)}
                          title="Remove"
                        >
                          <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sort By and Sort Direction — checks main page only */}
          {!isClarifications && !isDitDrl && !isUnknownPage && !isTrashPage && (
            <>
              <div className="filter-group">
                <label>Sort By</label>
                <SearchableDropdown
                  options={[
                    { value: '', label: 'Default Order' },
                    { value: 'DEPOSIT_DATE', label: 'Deposit Date' },
                    { value: 'RECEIVED_DATE', label: 'Received Date' },
                    { value: 'COMPLETED_DATE', label: 'Completed Date' },
                    { value: 'UPDATED_AT', label: 'Updated At' },
                    { value: 'CREATED_AT', label: 'Created At' },
                    { value: 'TOTAL_AMOUNT', label: 'Total Amount' },
                    { value: 'POSTED_AMOUNT', label: 'Posted Amount' },
                    { value: 'REMAINING_AMOUNT', label: 'Remaining Amount' }
                  ]}
                  value={localFilters.sortBy || ''}
                  onChange={(value) => {
                    handleChange('sortBy', value);
                    if (!value) handleChange('sortDirection', '');
                  }}
                  placeholder="Default Order"
                  maxVisibleItems={8}
                />
              </div>
              <div className="filter-group">
                <label>Sort Direction</label>
                <SearchableDropdown
                  options={[
                    { value: '', label: 'Select Direction' },
                    { value: 'ASC', label: 'Ascending' },
                    { value: 'DESC', label: 'Descending' }
                  ]}
                  value={localFilters.sortDirection || ''}
                  onChange={(value) => handleChange('sortDirection', value)}
                  placeholder="Select Direction"
                  maxVisibleItems={3}
                  disabled={!localFilters.sortBy}
                />
              </div>
            </>
          )}

          <div className="filter-group">
            {renderFilterLabel('assigneeIds', 'Assignee Name')}
            <div className="multi-select-wrapper">
              <div className="multi-select-input">
                <SearchableDropdown
                  options={[
                    { value: '', label: 'Select Assignee' },
                    ...users
                      .filter(user => !getSelectedValues('assigneeIds').includes(user.userId || user.id))
                      .map(user => ({
                        value: user.userId || user.id,
                        label: user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user.email || 'Unknown User'
                      }))
                  ]}
                  value=""
                  onChange={(value) => {
                    if (value) {
                      handleAddFilterValue('assigneeIds', value);
                    }
                  }}
                  placeholder="Select Assignee"
                  maxVisibleItems={5}
                />
              </div>
              {getSelectedValues('assigneeIds').length > 0 && (
                <div className="selected-chips">
                  {getSelectedValues('assigneeIds').map((assigneeId) => {
                    const user = users.find(u => (u.userId || u.id) === assigneeId);
                    const label = user && user.firstName && user.lastName 
                      ? `${user.firstName} ${user.lastName}` 
                      : user?.email || assigneeId;
                    return (
                      <div key={assigneeId} className="selected-chip">
                        <span>{isExcludeMode('assigneeIds') ? `Not ${label}` : label}</span>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          onClick={() => handleRemoveFilterValue('assigneeIds', assigneeId)}
                          title="Remove"
                        >
                          <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            {(() => {
              const reporterField = isDitDrl ? 'reporteeIds' : 'reporterIds';
              return renderFilterLabel(reporterField, 'Reporter Name');
            })()}
            <div className="multi-select-wrapper">
              <div className="multi-select-input">
                <SearchableDropdown
                  options={[
                    { value: '', label: 'Select Reporter' },
                    ...users
                      .filter(user => {
                        const filterKey = isDitDrl ? 'reporteeIds' : 'reporterIds';
                        return !getSelectedValues(filterKey).includes(user.userId || user.id);
                      })
                      .map(user => ({
                        value: user.userId || user.id,
                        label: user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user.email || 'Unknown User'
                      }))
                  ]}
                  value=""
                  onChange={(value) => {
                    if (value) {
                      const filterKey = isDitDrl ? 'reporteeIds' : 'reporterIds';
                      handleAddFilterValue(filterKey, value);
                    }
                  }}
                  placeholder="Select Reporter"
                  maxVisibleItems={5}
                />
              </div>
              {(() => {
                const filterKey = isDitDrl ? 'reporteeIds' : 'reporterIds';
                const reporterIds = getSelectedValues(filterKey);
                return reporterIds && reporterIds.length > 0 && (
                  <div className="selected-chips">
                    {reporterIds.map((reporterId) => {
                      const user = users.find(u => (u.userId || u.id) === reporterId);
                      const label = user && user.firstName && user.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : user?.email || reporterId;
                      return (
                        <div key={reporterId} className="selected-chip">
                          <span>{isExcludeMode(filterKey) ? `Not ${label}` : label}</span>
                          <button
                            type="button"
                            className="chip-remove-btn"
                            onClick={() => handleRemoveFilterValue(filterKey, reporterId)}
                            title="Remove"
                          >
                            <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {(isClarifications || isDitDrl) && (
            <>
              <div className="filter-group">
                <label>Assignee</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'Select Assignee Type' },
                        { value: 'ONSHORE', label: 'Onshore' },
                        { value: 'OFFSHORE', label: 'Offshore' }
                      ].filter(opt => opt.value === '' || !localFilters.assigneeTypes || !localFilters.assigneeTypes.includes(opt.value))}
                      value=""
                      onChange={(value) => {
                        if (value) {
                          handleAddToArray('assigneeTypes', value);
                        }
                      }}
                      placeholder="Select Assignee Type"
                      maxVisibleItems={3}
                    />
                  </div>
                  {localFilters.assigneeTypes && localFilters.assigneeTypes.length > 0 && (
                    <div className="selected-chips">
                      {localFilters.assigneeTypes.map((assigneeType) => {
                        const assigneeTypeLabels = {
                          'ONSHORE': 'Onshore',
                          'OFFSHORE': 'Offshore'
                        };
                        return (
                          <div key={assigneeType} className="selected-chip">
                            <span>{assigneeTypeLabels[assigneeType] || assigneeType}</span>
                            <button
                              type="button"
                              className="chip-remove-btn"
                              onClick={() => handleRemoveFromArray('assigneeTypes', assigneeType)}
                              title="Remove"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="filter-group">
                <label>Reporter</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'Select Reporter Type' },
                        { value: 'ONSHORE', label: 'Onshore' },
                        { value: 'OFFSHORE', label: 'Offshore' }
                      ].filter(opt => opt.value === '' || !localFilters.reporteeTypes || !localFilters.reporteeTypes.includes(opt.value))}
                      value=""
                      onChange={(value) => {
                        if (value) {
                          handleAddToArray('reporteeTypes', value);
                        }
                      }}
                      placeholder="Select Reporter Type"
                      maxVisibleItems={3}
                    />
                  </div>
                  {localFilters.reporteeTypes && localFilters.reporteeTypes.length > 0 && (
                    <div className="selected-chips">
                      {localFilters.reporteeTypes.map((reporteeType) => {
                        const reporteeTypeLabels = {
                          'ONSHORE': 'Onshore',
                          'OFFSHORE': 'Offshore'
                        };
                        return (
                          <div key={reporteeType} className="selected-chip">
                            <span>{reporteeTypeLabels[reporteeType] || reporteeType}</span>
                            <button
                              type="button"
                              className="chip-remove-btn"
                              onClick={() => handleRemoveFromArray('reporteeTypes', reporteeType)}
                              title="Remove"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {isClarifications && (
            <div className="filter-group">
              {renderFilterLabel('clarificationType', 'Clarification Type')}
              <div className="multi-select-wrapper">
                <div className="multi-select-input">
                  <SearchableDropdown
                    options={[
                      { value: '', label: 'Select Clarification Type' },
                      ...clarificationTypes.map(ct => ({
                        value: ct.code,
                        label: ct.name
                      }))
                    ].filter(opt => opt.value === '' || !getSelectedValues('clarificationType').includes(opt.value))}
                    value=""
                    onChange={(value) => {
                      if (value) handleAddFilterValue('clarificationType', value);
                    }}
                    placeholder="Select Clarification Type"
                    maxVisibleItems={5}
                    disabled={clarificationTypesLoading}
                  />
                </div>
                {getSelectedValues('clarificationType').length > 0 && (
                  <div className="selected-chips">
                    {getSelectedValues('clarificationType').map((clarType) => {
                      const item = clarificationTypes.find(ct => ct.code === clarType);
                      const label = item?.name || clarType;
                      return (
                        <div key={clarType} className="selected-chip">
                          <span>{isExcludeMode('clarificationType') ? `Not ${label}` : label}</span>
                          <button
                            type="button"
                            className="chip-remove-btn"
                            onClick={() => handleRemoveFilterValue('clarificationType', clarType)}
                            title="Remove"
                          >
                            <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}


          {isDitDrl ? (
            <>
              <div className="filter-group">
                <label>DRL Number</label>
                <input
                  type="text"
                  value={localFilters.drlNumber || ''}
                  onChange={(e) => {
                    const filteredValue = filterEmojis(e.target.value);
                    handleChange('drlNumber', filteredValue);
                  }}
                  placeholder="Enter DRL number (e.g., DRL-* or DRL-001)"
                />
              </div>

              {!isClarifications && (
                <div className="filter-group">
                  <label>Batch Number</label>
                  <input
                    type="text"
                    value={localFilters.batchNumber || ''}
                    onChange={(e) => {
                      const filteredValue = filterEmojis(e.target.value);
                      handleChange('batchNumber', filteredValue);
                    }}
                    placeholder="Enter batch number (e.g., B-00* or B-00)"
                  />
                </div>
              )}

              <div className="filter-group">
                <label>Reconciliation Status</label>
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'Select Reconciliation Status' },
                        { value: 'RECONCILED', label: 'Reconciled' },
                        { value: 'OVER_RECONCILED', label: 'Excess Reconciled' },
                        { value: 'UNRECONCILED', label: 'Pending Reconciliation' }
                      ].filter(option => 
                        option.value === '' || 
                        !localFilters.reconciliationStatuses || 
                        !localFilters.reconciliationStatuses.includes(option.value)
                      )}
                      value=""
                      onChange={(value) => {
                        if (value) {
                          handleAddToArray('reconciliationStatuses', value);
                        }
                      }}
                      placeholder="Select Reconciliation Status"
                      maxVisibleItems={4}
                    />
                  </div>
                  {localFilters.reconciliationStatuses && localFilters.reconciliationStatuses.length > 0 && (
                    <div className="selected-chips">
                      {localFilters.reconciliationStatuses.map((status) => {
                        const statusLabels = {
                          'RECONCILED': 'Reconciled',
                          'OVER_RECONCILED': 'Excess Reconciled',
                          'UNRECONCILED': 'Pending Reconciliation'
                        };
                        return (
                          <div key={status} className="selected-chip">
                            <span>{statusLabels[status] || status}</span>
                            <button
                              type="button"
                              className="chip-remove-btn"
                              onClick={() => handleRemoveFromArray('reconciliationStatuses', status)}
                              title="Remove"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="filter-group">
                <label>Check Number</label>
                <input
                  type="text"
                  value={localFilters.checkNumber || ''}
                  onChange={(e) => handleChange('checkNumber', e.target.value)}
                  placeholder="Enter check number (e.g., ch* or ch)"
                />
              </div>

              {!isDitDrl && !isClarifications && (
                <div className="filter-group">
                  <label>Batch Number</label>
                  <input
                    type="text"
                    value={localFilters.batchNumber || ''}
                    onChange={(e) => {
                      const filteredValue = filterEmojis(e.target.value);
                      handleChange('batchNumber', filteredValue);
                    }}
                    placeholder="Enter batch number (e.g., B-00* or B-00)"
                  />
                </div>
              )}

              {!isDitDrl && (
                <div className="filter-group">
                  {renderFilterLabel('checkTypes', 'Check Type')}
                  <div className="multi-select-wrapper">
                    <div className="multi-select-input">
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Check Type' },
                          ...checkTypes.map(ct => ({
                            value: ct.code,
                            label: ct.name
                          }))
                        ].filter(opt => opt.value === '' || !getSelectedValues('checkTypes').includes(opt.value))}
                        value=""
                        onChange={(value) => {
                          if (value) handleAddFilterValue('checkTypes', value);
                        }}
                        placeholder="Select Check Type"
                        maxVisibleItems={5}
                        disabled={checkTypesLoading}
                      />
                    </div>
                    {getSelectedValues('checkTypes').length > 0 && (
                      <div className="selected-chips">
                        {getSelectedValues('checkTypes').map((checkType) => {
                          const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
                          const label = checkTypeItem?.name || checkType;
                          return (
                            <div key={checkType} className="selected-chip">
                              <span>{isExcludeMode('checkTypes') ? `Not ${label}` : label}</span>
                              <button
                                type="button"
                                className="chip-remove-btn"
                                onClick={() => handleRemoveFilterValue('checkTypes', checkType)}
                                title="Remove"
                              >
                                <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!isDitDrl && (
                <div className="filter-group">
                  {renderFilterLabel('workflowStatuses', 'Workflow Status')}
                  <div className="multi-select-wrapper">
                    <div className="multi-select-input">
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Workflow Status' },
                          ...workflowStatuses.map(ws => ({
                            value: ws.code,
                            label: ws.name
                          }))
                        ].filter(opt => opt.value === '' || !getSelectedValues('workflowStatuses').includes(opt.value))}
                        value=""
                        onChange={(value) => {
                          if (value) handleAddFilterValue('workflowStatuses', value);
                        }}
                        placeholder="Select Workflow Status"
                        maxVisibleItems={5}
                        disabled={workflowStatusesLoading}
                      />
                    </div>
                    {getSelectedValues('workflowStatuses').length > 0 && (
                      <div className="selected-chips">
                        {getSelectedValues('workflowStatuses').map((workflowStatus) => {
                          const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
                          const label = workflowStatusItem?.name || workflowStatus;
                          return (
                            <div key={workflowStatus} className="selected-chip">
                              <span>{isExcludeMode('workflowStatuses') ? `Not ${label}` : label}</span>
                              <button
                                type="button"
                                className="chip-remove-btn"
                                onClick={() => handleRemoveFilterValue('workflowStatuses', workflowStatus)}
                                title="Remove"
                              >
                                <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {isClarifications ? (
                <>
                  <div className="filter-group">
                    <label>Unknown</label>
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'All' },
                        { value: 'false', label: 'False' },
                        { value: 'true', label: 'True' }
                      ]}
                      value={localFilters.unknown === undefined ? '' : (localFilters.unknown === true ? 'true' : 'false')}
                      onChange={(value) => {
                        if (value === '') {
                          handleChange('unknown', undefined);
                        } else {
                          handleChange('unknown', value === 'true');
                        }
                      }}
                      placeholder="All"
                      maxVisibleItems={3}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="filter-group">
                    <label>Payer</label>
                    <input
                      type="text"
                      value={localFilters.payer || ''}
                      onChange={(e) => handleChange('payer', e.target.value)}
                      placeholder="Enter payer name (e.g., *blue* or blue)"
                    />
                  </div>

                  {!isUnknownPage && (
                    <div className="filter-group">
                      <label>Unknown</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'All' },
                          { value: 'false', label: 'False' },
                          { value: 'true', label: 'True' }
                        ]}
                        value={localFilters.unknown === undefined ? '' : (localFilters.unknown === true ? 'true' : 'false')}
                        onChange={(value) => {
                          if (value === '') {
                            handleChange('unknown', undefined);
                          } else {
                            handleChange('unknown', value === 'true');
                          }
                        }}
                        placeholder="All"
                        maxVisibleItems={3}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {isClarifications ? (
            <>
              {isDitDrl ? (
                <>
                  <div className="filter-group">
                    <label>DRL Received Date From</label>
                    <USDateInput
                      name="drlReceivedDateFrom"
                      value={localFilters.drlReceivedDateFrom || ''}
                      onChange={(e) => handleDateChange('drlReceivedDateFrom', e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      DRL Received Date To
                      <Tooltip text="Select start date to enable" position="top">
                        <Info 
                          size={14} 
                          style={{ 
                            color: '#6b7280', 
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            strokeWidth: 2.5,
                            flexShrink: 0,
                            marginBottom: '2px'
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                          onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                        />
                      </Tooltip>
                    </label>
                    <USDateInput
                      ref={dateReceivedToRef}
                      name="drlReceivedDateTo"
                      value={localFilters.drlReceivedDateTo || ''}
                      onChange={(e) => handleDateChange('drlReceivedDateTo', e.target.value)}
                      min={localFilters.drlReceivedDateFrom || undefined}
                      max={calculateMaxDate(localFilters.drlReceivedDateFrom)}
                      disabled={!localFilters.drlReceivedDateFrom || localFilters.drlReceivedDateFrom.trim() === ''}
                      onDisabledClick={() => showError('First you need to select start date for DRL Received Date.')}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="filter-group">
                    <label>Deposit Date From</label>
                    <USDateInput
                      name="depositDateFrom"
                      value={localFilters.depositDateFrom || ''}
                      onChange={(e) => handleDateChange('depositDateFrom', e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Deposit Date To
                      <Tooltip text="Select start date to enable" position="top">
                        <Info 
                          size={14} 
                          style={{ 
                            color: '#6b7280', 
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            strokeWidth: 2.5,
                            flexShrink: 0,
                            marginBottom: '2px'
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                          onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                        />
                      </Tooltip>
                    </label>
                    <USDateInput
                      ref={depositDateToRef}
                      name="depositDateTo"
                      value={localFilters.depositDateTo || ''}
                      onChange={(e) => handleDateChange('depositDateTo', e.target.value)}
                      min={localFilters.depositDateFrom || undefined}
                      max={calculateMaxDate(localFilters.depositDateFrom)}
                      disabled={!localFilters.depositDateFrom || localFilters.depositDateFrom.trim() === ''}
                      onDisabledClick={() => showError('First you need to select start date for Deposit Date.')}
                    />
                  </div>
                </>
              )}

              <div className="filter-group">
                <label>Resolved Date From</label>
                <USDateInput
                  name="resolvedDateFrom"
                  value={localFilters.resolvedDateFrom || ''}
                  onChange={(e) => handleDateChange('resolvedDateFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Resolved Date To{localFilters.resolvedDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={resolvedDateToRef}
                  name="resolvedDateTo"
                  value={localFilters.resolvedDateTo || ''}
                  onChange={(e) => handleDateChange('resolvedDateTo', e.target.value)}
                  min={localFilters.resolvedDateFrom || undefined}
                  max={calculateMaxDate(localFilters.resolvedDateFrom)}
                  disabled={!localFilters.resolvedDateFrom || localFilters.resolvedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Resolved Date.')}
                />
              </div>

              <div className="filter-group">
                <label>Opened Date From</label>
                <USDateInput
                  name="openedDateFrom"
                  value={localFilters.openedDateFrom || ''}
                  onChange={(e) => handleDateChange('openedDateFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Opened Date To{localFilters.openedDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={openedDateToRef}
                  name="openedDateTo"
                  value={localFilters.openedDateTo || ''}
                  onChange={(e) => handleDateChange('openedDateTo', e.target.value)}
                  min={localFilters.openedDateFrom || undefined}
                  max={calculateMaxDate(localFilters.openedDateFrom)}
                  disabled={!localFilters.openedDateFrom || localFilters.openedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Opened Date.')}
                />
              </div>
            </>
          ) : isDitDrl ? (
            <>
              <div className="filter-group">
                <label>DRL Received From</label>
                <USDateInput
                  name="drlReceivedFrom"
                  value={localFilters.drlReceivedFrom || ''}
                  onChange={(e) => handleDateChange('drlReceivedFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  DRL Received To{localFilters.drlReceivedFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={dateReceivedToRef}
                  name="drlReceivedTo"
                  value={localFilters.drlReceivedTo || ''}
                  onChange={(e) => handleDateChange('drlReceivedTo', e.target.value)}
                  min={localFilters.drlReceivedFrom || undefined}
                  max={calculateMaxDate(localFilters.drlReceivedFrom)}
                  disabled={!localFilters.drlReceivedFrom || localFilters.drlReceivedFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for DRL Received.')}
                />
              </div>

              <div className="filter-group">
                <label>DRL Created From</label>
                <USDateInput
                  name="createdAtFrom"
                  value={localFilters.createdAtFrom || ''}
                  onChange={(e) => handleDateChange('createdAtFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  DRL Created To{localFilters.createdAtFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={createdAtToRef}
                  name="createdAtTo"
                  value={localFilters.createdAtTo || ''}
                  onChange={(e) => handleDateChange('createdAtTo', e.target.value)}
                  min={localFilters.createdAtFrom || undefined}
                  max={calculateMaxDate(localFilters.createdAtFrom)}
                  disabled={!localFilters.createdAtFrom || localFilters.createdAtFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for DRL Created.')}
                />
              </div>

              <div className="filter-group">
                <label>Posting Completed From</label>
                <USDateInput
                  name="completedDateFrom"
                  value={localFilters.completedDateFrom || ''}
                  onChange={(e) => handleDateChange('completedDateFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Posting Completed To{localFilters.completedDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={completedDateToRefDrl}
                  name="completedDateTo"
                  value={localFilters.completedDateTo || ''}
                  onChange={(e) => handleDateChange('completedDateTo', e.target.value)}
                  min={localFilters.completedDateFrom || undefined}
                  max={calculateMaxDate(localFilters.completedDateFrom)}
                  disabled={!localFilters.completedDateFrom || localFilters.completedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Posting Completed.')}
                />
              </div>

              <div className="filter-group">
                <label>Reconciliation From</label>
                <USDateInput
                  name="reconciliationDateFrom"
                  value={localFilters.reconciliationDateFrom || ''}
                  onChange={(e) => handleDateChange('reconciliationDateFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Reconciliation To{localFilters.reconciliationDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={reconciliationDateToRef}
                  name="reconciliationDateTo"
                  value={localFilters.reconciliationDateTo || ''}
                  onChange={(e) => handleDateChange('reconciliationDateTo', e.target.value)}
                  min={localFilters.reconciliationDateFrom || undefined}
                  max={calculateMaxDate(localFilters.reconciliationDateFrom)}
                  disabled={!localFilters.reconciliationDateFrom || localFilters.reconciliationDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Reconciliation.')}
                />
              </div>
            </>
          ) : (
            <>
              <div className="filter-group">
                <label>Deposit Date From</label>
                <USDateInput
                  name="depositDateFrom"
                  value={localFilters.depositDateFrom || ''}
                  onChange={(e) => handleDateChange('depositDateFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Deposit Date To{localFilters.depositDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={depositDateToRef}
                  name="depositDateTo"
                  value={localFilters.depositDateTo || ''}
                  onChange={(e) => handleDateChange('depositDateTo', e.target.value)}
                  min={localFilters.depositDateFrom || undefined}
                  max={calculateMaxDate(localFilters.depositDateFrom)}
                  disabled={!localFilters.depositDateFrom || localFilters.depositDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Deposit Date.')}
                />
              </div>

              <div className="filter-group">
                <label>Received Date From</label>
                <USDateInput
                  name="receivedDateFrom"
                  value={localFilters.receivedDateFrom || ''}
                  onChange={(e) => handleDateChange('receivedDateFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Received Date To{localFilters.receivedDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={receivedDateToRef}
                  name="receivedDateTo"
                  value={localFilters.receivedDateTo || ''}
                  onChange={(e) => handleDateChange('receivedDateTo', e.target.value)}
                  min={localFilters.receivedDateFrom || undefined}
                  max={calculateMaxDate(localFilters.receivedDateFrom)}
                  disabled={!localFilters.receivedDateFrom || localFilters.receivedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Received Date.')}
                />
              </div>

              <div className="filter-group">
                <label>Completed Date From</label>
                <USDateInput
                  name="completedDateFrom"
                  value={localFilters.completedDateFrom || ''}
                  onChange={(e) => handleDateChange('completedDateFrom', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Completed Date To{localFilters.completedDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={completedDateToRef}
                  name="completedDateTo"
                  value={localFilters.completedDateTo || ''}
                  onChange={(e) => handleDateChange('completedDateTo', e.target.value)}
                  min={localFilters.completedDateFrom || undefined}
                  max={calculateMaxDate(localFilters.completedDateFrom)}
                  disabled={!localFilters.completedDateFrom || localFilters.completedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Completed Date.')}
                />
              </div>
            </>
          )}

          {/* Month and Year filters removed from Advanced Filter as requested */}
        </div>

        <div className="drawer-footer">
          <button className="btn-reset" onClick={handleReset}>
            Reset Filters
          </button>
          {filterType && (
            <button className="btn-save-filter" onClick={() => { setSaveFilterName(''); setSaveFilterOrder(''); setShowSaveModal(true); }}>
              <BookmarkPlus size={15} />
              Save Filter
            </button>
          )}
          <button className="btn-apply" onClick={handleApply}>
            Apply Filters
          </button>
        </div>
      </div>

      {/* Save Filter Name Modal */}
      {showSaveModal && (
        <div className="save-filter-modal-overlay" onClick={() => !isSaving && setShowSaveModal(false)}>
          <div className="save-filter-modal" onClick={e => e.stopPropagation()}>
            <div className="save-filter-modal-header">
              <BookmarkPlus size={18} />
              <h3>Save Current Filter</h3>
              <button className="save-filter-modal-close" onClick={() => setShowSaveModal(false)} disabled={isSaving}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="save-filter-modal-body">
              <label className="save-filter-label">Filter Name <span className="save-filter-required">*</span></label>
              <input
                type="text"
                className="save-filter-input"
                placeholder="e.g. Active Checks – Q1 2025"
                value={saveFilterName}
                onChange={e => setSaveFilterName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveFilter(); if (e.key === 'Escape') setShowSaveModal(false); }}
                autoFocus
                maxLength={100}
                disabled={isSaving}
              />
              <label className="save-filter-label" style={{ marginTop: '14px' }}>Sort Order <span className="save-filter-hint-text">(optional)</span></label>
              <input
                type="number"
                className="save-filter-input"
                placeholder="e.g. 1, 2, 3…"
                value={saveFilterOrder}
                onChange={e => setSaveFilterOrder(e.target.value)}
                min={0}
                disabled={isSaving}
              />
            </div>
            <div className="save-filter-modal-footer">
              <button className="save-filter-btn-cancel" onClick={() => setShowSaveModal(false)} disabled={isSaving}>
                Cancel
              </button>
              <button className="save-filter-btn-confirm" onClick={handleSaveFilter} disabled={isSaving || !saveFilterName.trim()}>
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdvancedFilterDrawer;

